import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  DollarSign,
  ArrowLeft,
  CheckCircle,
  Clock,
  Calendar,
  User,
  Building2,
  GraduationCap,
  FileText,
  CreditCard,
  AlertCircle,
} from 'lucide-react';
import { agentApi } from '@/services/agentApi';
import { AgentCommission } from '@/types/user';

export const CommissionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [commission, setCommission] = useState<AgentCommission | null>(null);
  const [loading, setLoading] = useState(true);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);

  useEffect(() => {
    if (id) {
      loadCommission();
    }
  }, [id]);

  const loadCommission = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await agentApi.getCommission(id);
      setCommission(data);
    } catch (error) {
      console.error('Error loading commission:', error);
      toast.error('Failed to load commission details');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!id || !commission) return;

    if (commission.status === 'PAID') {
      toast.error('This commission has already been paid.');
      return;
    }

    if (!window.confirm('Are you sure you want to request a payout for this commission?')) {
      return;
    }

    setRequestingPayout(true);
    try {
      await agentApi.requestPayout(id);
      toast.success('Payout request submitted successfully!');
      setShowPayoutModal(false);
      // Reload commission to get updated status
      await loadCommission();
    } catch (error: any) {
      console.error('Error requesting payout:', error);
      toast.error(error.response?.data?.error || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!commission) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center py-5">
          <AlertCircle size={48} className="text-muted mb-3" />
          <h5 className="text-muted">Commission not found</h5>
          <p className="text-muted">The commission you're looking for doesn't exist.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/commissions')}>
            Back to Commissions
          </button>
        </div>
      </div>
    );
  }

  const application = commission.agent_student_applications;

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col">
          <button
            className="btn btn-link text-dark p-0 mb-3 d-flex align-items-center"
            onClick={() => navigate('/commissions')}
          >
            <ArrowLeft size={20} className="me-2" />
            Back to Commissions
          </button>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <h1 className="h3 fw-bold text-dark mb-1">Commission Details</h1>
              <p className="text-muted mb-0">Reference: {commission.reference || `#${commission.id}`}</p>
            </div>
            {commission.status === 'PENDING' && (
              <button
                className="btn btn-primary"
                onClick={() => setShowPayoutModal(true)}
                disabled={requestingPayout}
              >
                <CreditCard size={18} className="me-2" />
                Request Payout
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="row">
        {/* Main Details */}
        <div className="col-lg-8">
          {/* Commission Info Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card border-0 shadow-sm mb-4"
          >
            <div className="card-body">
              <h5 className="card-title mb-4">Commission Information</h5>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-3 me-3">
                      <DollarSign size={24} className="text-primary" />
                    </div>
                    <div>
                      <small className="text-muted d-block">Amount</small>
                      <h4 className="fw-bold text-success mb-0">
                        {formatCurrency(commission.amount)}
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-info bg-opacity-10 rounded-circle p-3 me-3">
                      <FileText size={24} className="text-info" />
                    </div>
                    <div>
                      <small className="text-muted d-block">Commission Rate</small>
                      <h4 className="fw-bold mb-0">{(commission.rate * 100).toFixed(2)}%</h4>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-warning bg-opacity-10 rounded-circle p-3 me-3">
                      {commission.status === 'PAID' ? (
                        <CheckCircle size={24} className="text-success" />
                      ) : (
                        <Clock size={24} className="text-warning" />
                      )}
                    </div>
                    <div>
                      <small className="text-muted d-block">Status</small>
                      <h5 className="fw-bold mb-0">
                        {commission.status === 'PAID' ? (
                          <span className="badge bg-success">Paid</span>
                        ) : (
                          <span className="badge bg-warning">Pending</span>
                        )}
                      </h5>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="d-flex align-items-center mb-3">
                    <div className="bg-secondary bg-opacity-10 rounded-circle p-3 me-3">
                      <Calendar size={24} className="text-secondary" />
                    </div>
                    <div>
                      <small className="text-muted d-block">
                        {commission.status === 'PAID' ? 'Paid Date' : 'Created Date'}
                      </small>
                      <h6 className="fw-bold mb-0">
                        {commission.status === 'PAID' && commission.paidAt
                          ? formatDate(commission.paidAt)
                          : formatDate(commission.createdAt)}
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Payment Details Card - Only show if paid */}
          {commission.status === 'PAID' && (commission.paymentMethod || commission.paymentReference || commission.notes) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card border-0 shadow-sm mb-4"
              style={{ borderLeft: '4px solid #10b981' }}
            >
              <div className="card-body">
                <h5 className="card-title mb-4">Payment Details</h5>
                <div className="row g-4">
                  {commission.paymentMethod && (
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <CreditCard size={20} className="text-muted me-3 mt-1" />
                        <div>
                          <small className="text-muted d-block">Payment Method</small>
                          <div className="fw-semibold">{commission.paymentMethod}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {commission.paymentReference && (
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <FileText size={20} className="text-muted me-3 mt-1" />
                        <div>
                          <small className="text-muted d-block">Payment Reference</small>
                          <div className="fw-semibold">{commission.paymentReference}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  {commission.notes && (
                    <div className="col-12">
                      <div className="d-flex align-items-start mb-3">
                        <FileText size={20} className="text-muted me-3 mt-1" />
                        <div className="flex-grow-1">
                          <small className="text-muted d-block mb-2">Notes</small>
                          <div className="bg-light p-3 rounded border">
                            <p className="mb-0 text-dark">{commission.notes}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Application Details Card */}
          {application && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="card border-0 shadow-sm mb-4"
            >
              <div className="card-body">
                <h5 className="card-title mb-4">Application Details</h5>
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="d-flex align-items-start mb-3">
                      <FileText size={20} className="text-muted me-3 mt-1" />
                      <div>
                        <small className="text-muted d-block">Application Reference</small>
                        <div className="fw-semibold">{application.reference}</div>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="d-flex align-items-start mb-3">
                      <User size={20} className="text-muted me-3 mt-1" />
                      <div>
                        <small className="text-muted d-block">Student Name</small>
                        <div className="fw-semibold">
                          {application.firstName} {application.lastName}
                        </div>
                        <small className="text-muted">{application.email}</small>
                      </div>
                    </div>
                  </div>
                  {application.universities && (
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <Building2 size={20} className="text-muted me-3 mt-1" />
                        <div>
                          <small className="text-muted d-block">University</small>
                          <div className="fw-semibold">{application.universities.name}</div>
                          <small className="text-muted">
                            {application.universities.city}, {application.universities.country}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}
                  {application.university_programs?.programs && (
                    <div className="col-md-6">
                      <div className="d-flex align-items-start mb-3">
                        <GraduationCap size={20} className="text-muted me-3 mt-1" />
                        <div>
                          <small className="text-muted d-block">Program</small>
                          <div className="fw-semibold">
                            {application.university_programs.programs.name}
                          </div>
                          <small className="text-muted">
                            {application.university_programs.programs.level}
                          </small>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => navigate(`/applications/${application.id}`)}
                  >
                    View Full Application
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Summary Sidebar */}
        <div className="col-lg-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="card border-0 shadow-sm"
          >
            <div className="card-body">
              <h5 className="card-title mb-4">Summary</h5>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Commission Reference</small>
                <div className="fw-semibold">{commission.reference || `#${commission.id}`}</div>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Application Reference</small>
                <div className="fw-semibold">{application?.reference || `#${commission.applicationId}`}</div>
              </div>
              <div className="mb-3">
                <small className="text-muted d-block mb-1">Created</small>
                <div className="fw-semibold">{formatDate(commission.createdAt)}</div>
              </div>
              {commission.updatedAt && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Last Updated</small>
                  <div className="fw-semibold">{formatDate(commission.updatedAt)}</div>
                </div>
              )}
              {commission.status === 'PAID' && commission.paidAt && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Paid On</small>
                  <div className="fw-semibold text-success">{formatDate(commission.paidAt)}</div>
                </div>
              )}
              {commission.status === 'PAID' && commission.paymentMethod && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Payment Method</small>
                  <div className="fw-semibold">{commission.paymentMethod}</div>
                </div>
              )}
              {commission.status === 'PAID' && commission.paymentReference && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Payment Reference</small>
                  <div className="fw-semibold small">{commission.paymentReference}</div>
                </div>
              )}
              {commission.notes && (
                <div className="mb-3">
                  <small className="text-muted d-block mb-1">Notes</small>
                  <div className="fw-semibold small" style={{ whiteSpace: 'pre-wrap' }}>{commission.notes}</div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Payout Request Modal */}
      {showPayoutModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center px-3"
          style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)', zIndex: 1050, backdropFilter: 'blur(2px)' }}
          onClick={() => setShowPayoutModal(false)}
        >
          <div
            className="bg-white rounded-3 shadow-lg"
            style={{ maxWidth: '480px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 pb-0 d-flex justify-content-between align-items-start">
              <div className="d-flex align-items-center">
                <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                  <CreditCard size={20} className="text-primary" />
                </div>
                <div>
                  <h5 className="mb-1">Request Payout</h5>
                  <p className="text-muted mb-0 small">
                    Review the details below before submitting your payout request.
                  </p>
                </div>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowPayoutModal(false)}
              ></button>
            </div>
            <div className="p-4 pt-3">
              <div className="border rounded-3 p-3 mb-3 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">Commission amount</span>
                  <span className="fw-bold text-success">{formatCurrency(commission.amount)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted small">Current status</span>
                  <span className="badge bg-warning text-dark text-uppercase small">
                    {commission.status.toLowerCase()}
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted small">Reference</span>
                  <span className="fw-semibold small">
                    {commission.reference || `#${commission.id}`}
                  </span>
                </div>
              </div>
              <p className="text-muted small mb-0">
                By confirming, you are requesting that this commission be paid out to you. Our team
                will review your request and notify you once the payout has been processed.
              </p>
            </div>
            <div className="px-4 pb-4 pt-2 d-flex flex-column flex-sm-row justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-outline-secondary w-100 w-sm-auto"
                onClick={() => setShowPayoutModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary w-100 w-sm-auto"
                onClick={handleRequestPayout}
                disabled={requestingPayout}
              >
                {requestingPayout ? 'Submitting request…' : 'Confirm payout request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

